import { useState } from 'react';

const FALLBACK_FOLDERS = [
  { id: 'inbox', label: 'Inbox', count: 0, icon: 'inbox' },
  { id: 'starred', label: 'Starred', icon: 'star' },
  { id: 'snoozed', label: 'Snoozed', icon: 'clock' },
  { id: 'sent', label: 'Sent', icon: 'send' },
  { id: 'drafts', label: 'Drafts', count: 0, icon: 'file' },
  { id: 'important', label: 'Important', icon: 'bookmark' },
  { id: 'spam', label: 'Spam', count: 0, icon: 'shield' },
  { id: 'trash', label: 'Trash', icon: 'trash' },
];

const FALLBACK_LABELS = [];

export function MailSidebar({
  activeFolder = 'inbox',
  onSelectFolder,
  activeLabel = null,
  onSelectLabel,
  onComposeClick,
  labelsData = {},
}) {
  const [showMoreLabels, setShowMoreLabels] = useState(false);

  const folders = labelsData?.folders?.length
    ? labelsData.folders
    : FALLBACK_FOLDERS;
  const labels = labelsData?.labels?.length
    ? labelsData.labels
    : FALLBACK_LABELS;

  const renderFolderIcon = (iconName) => {
    switch (iconName) {
      case 'inbox':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
            <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
          </svg>
        );
      case 'star':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        );
      case 'clock':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        );
      case 'send':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        );
      case 'file':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        );
      case 'bookmark':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
          </svg>
        );
      case 'shield':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        );
      case 'trash':
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <aside className="w-full lg:w-56 shrink-0 bg-white p-3 flex flex-col h-full overflow-y-auto select-none border-r border-gray-100">
      {/* Compose Button */}
      <button
        type="button"
        onClick={onComposeClick}
        className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2.5 shadow-sm transition-all duration-200 hover:shadow mb-4 cursor-pointer"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
        <span className="text-sm font-sf-pro-display tracking-wide">
          Compose
        </span>
      </button>

      {/* Folders List */}
      <nav className="space-y-1 mb-6">
        {folders.map((folder) => {
          const isActive = activeFolder === folder.id && !activeLabel;
          return (
            <button
              key={folder.id}
              type="button"
              onClick={() => {
                onSelectLabel?.(null);
                onSelectFolder?.(folder.id);
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium font-sf-pro-display transition-all ${
                isActive
                  ? 'bg-surface-mint text-primary font-bold'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={isActive ? 'text-primary' : 'text-gray-400'}>
                  {renderFolderIcon(folder.icon)}
                </span>
                <span>{folder.label}</span>
              </div>
              {folder.count > 0 && (
                <span
                  className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                    isActive
                      ? 'bg-primary/20 text-primary'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {folder.count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Labels Header */}
      <div className="flex items-center justify-between px-3 mb-2">
        <span className="text-xs font-bold font-sf-pro-display text-primary tracking-wide uppercase">
          Labels
        </span>
      </div>

      {/* Labels List */}
      <div className="space-y-0.5">
        {(showMoreLabels ? labels : labels.slice(0, 6)).map((label) => {
          const isActive = activeLabel === label.id;
          return (
            <button
              key={label.id}
              type="button"
              onClick={() => onSelectLabel?.(label.id)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium font-sf-pro-display transition-all ${
                isActive
                  ? 'bg-gray-100 text-gray-900 font-semibold'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`w-2.5 h-2.5 rounded-full ${label.color}`} />
                <span>{label.label}</span>
              </div>
              {label.count > 0 && (
                <span className="text-[11px] font-semibold text-gray-400">
                  {label.count}
                </span>
              )}
            </button>
          );
        })}

        {/* More Toggle */}
        <button
          type="button"
          onClick={() => setShowMoreLabels((p) => !p)}
          className="w-full flex items-center gap-3 px-3 py-2 text-sm font-medium font-sf-pro-display text-gray-500 hover:text-gray-800 hover:bg-gray-50 rounded-xl transition-all"
        >
          <svg
            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showMoreLabels ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
          <span>{showMoreLabels ? 'Less' : 'More'}</span>
        </button>
      </div>
    </aside>
  );
}
